
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      previousFlavor = null;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        console.log('connectedCallback')
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSellingPlanPriceSync();

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      updateProductTitleWithFlavor() {

        try {
          // Get the current flavor from the variant selectors
          const variantSelects = this.querySelector('variant-selects');
          if (!variantSelects) return;

          // Find the flavor option - look for any fieldset that might contain flavor options
          let flavorFieldset = null;
          const fieldsets = variantSelects.querySelectorAll('fieldset');
          
          for (const fieldset of fieldsets) {
            const legend = fieldset.querySelector('legend');
            if (legend && legend.textContent.toLowerCase().includes('flavor')) {
              flavorFieldset = fieldset;
              break;
            }
          }

          
          if (!flavorFieldset) return;

          const selectedFlavorInput = flavorFieldset.querySelector('input[type="radio"]:checked');
          if (!selectedFlavorInput) return;

          // Update the active swatch color on the body
          this.updateActiveSwatchColor(selectedFlavorInput);

          // Update the data-for-flavor attribute on the body
          this.updateBodyFlavorAttribute(selectedFlavorInput);
        } catch (error) {
          console.warn('Error updating product title with flavor:', error);
        }
      }

      updateProductTitle(html) {
        const sourceTitle = html.querySelector('.product__title');
        const destinationTitle = this.querySelector('.product__title');
        
        if (sourceTitle && destinationTitle) {
          destinationTitle.innerHTML = sourceTitle.innerHTML;
        }
      }

      updateActiveSwatchColor(selectedInput) {
        try {
          // Get the variant ID from the selected input
          const variantId = selectedInput.dataset.optionValueId;
          
          if (!variantId) {
            console.log('No variant ID found');
            return;
          }

          // Get the swatch color from the selected input's label
          const selectedLabel = selectedInput.nextElementSibling;
          if (!selectedLabel) {
            console.log('No label found for selected input');
            return;
          }

          // Get the swatch color from the label's style attribute
          const swatchColor = selectedLabel.style.getPropertyValue('--theme-active-swatch') || '#00B934';
          
          // Update the body's --theme-active-swatch CSS variable
          document.body.style.setProperty('--theme-active-swatch', swatchColor);
        } catch (error) {
          console.warn('Error updating active swatch color:', error);
        }
      }

      updateBodyFlavorAttribute(selectedInput) {
        try {
          // Get the flavor value from the selected input
          const flavorValue = selectedInput.value;
          
          if (!flavorValue) {
            console.log('No flavor value found');
            return;
          }

          // Format the flavor value: lowercase and replace spaces with dashes
          const formattedFlavor = flavorValue.toLowerCase().replace(/\s+/g, '-');
          
          // Update the data-for-flavor attribute on the body
          document.body.setAttribute('data-current-flavor', formattedFlavor);
        } catch (error) {
          console.warn('Error updating body flavor attribute:', error);
        }
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Update product title
          this.updateProductTitle(html);

          // Update buy buttons
          this.updateBuyButtons(html);
          this.updateBuyButtonPriceForSellingPlan();

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Removed conflicting toggleSubmitButton call since updateBuyButtons already handles the submit button state

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Update the active swatch color
          this.updateProductTitleWithFlavor();
          document.querySelector('product-form').init();
          
          // Check if flavor changed and refresh flavor-specific sections
          this.handleFlavorChange(variant);
          // this.setBuyButtonPrice()
        };
      }

      handleFlavorChange(variant) {
        
        // Check if the variant update was a change in flavor
        // Look for specific flavor values in the options array
        const flavorValues = ['Solar Mango', 'Pineapple Crush', 'Cherry Lime'];
        let flavorOption = variant.options?.find(option => 
          flavorValues.includes(option)
        );

        
        if (!flavorOption) {
          return;
        }

        const currentFlavor = flavorOption.toLowerCase().replace(/\s+/g, '-');
        
        // Only proceed if flavor actually changed
        if (this.previousFlavor === currentFlavor) {
          return;
        }
        
        this.previousFlavor = currentFlavor;
        
        // Dispatch custom event for flavor change
        // console.log('product-info: Dispatching product-flavor:refreshed event for flavor:', currentFlavor)
        this.dispatchEvent(new CustomEvent('product-flavor:refreshed', { 
          bubbles: true,
          detail: { 
            flavor: currentFlavor,
            previousFlavor: this.previousFlavor 
          }
        }));

        // Get all elements with data-for-flavor
        const flavorElements = document.querySelectorAll('[data-for-flavor]');
        
        if (flavorElements.length === 0) {
          return;
        }
        
        // Get their section IDs and filter out duplicates
        const sectionIds = [...new Set(
          Array.from(flavorElements)
            .map(element => element.dataset.sectionId)
            .filter(Boolean)
        )];

        // Refresh each section
        sectionIds.forEach(sectionId => {
          this.refreshFlavorSection(sectionId, currentFlavor);
        });
      }

      refreshFlavorSection(sectionId, currentFlavor) {
        const url = window.location.pathname;
      
        const requestUrl = `${url}?section_id=${sectionId}`;
        
        
        fetch(requestUrl)
          .then(response => {
            return response.text();
          })
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newSection = html.getElementById(`shopify-section-${sectionId}`);
            const currentSection = document.getElementById(`shopify-section-${sectionId}`);
            
            if (newSection && currentSection) {
              
              // Filter out blocks that don't match the current flavor
              const flavorElements = newSection.querySelectorAll('[data-for-flavor]');
              flavorElements.forEach(element => {
                const elementFlavor = element.dataset.forFlavor;
                if (elementFlavor && !elementFlavor.includes(currentFlavor)) {
                  element.remove();
                }
              });
              
              currentSection.innerHTML = newSection.innerHTML;
            }
          })
          .catch(error => {
            console.error('Error refreshing flavor section:', sectionId, error);
          });
      }

      updateBuyButtons(html) {
        // console.log('updateBuyButtons')
        const sourceBuyButtons = this.querySelector('product-form');
        const destinationBuyButtons = html.querySelector('product-form');
        
        if (sourceBuyButtons && destinationBuyButtons) {
          // Only update the variant ID input - preserve all other form elements and app functionality
          const sourceVariantInput = sourceBuyButtons.querySelector('input[name="id"]');
          const destinationVariantInput = destinationBuyButtons.querySelector('input[name="id"]');
          
          if (sourceVariantInput && destinationVariantInput) {
            const newVariantId = destinationVariantInput.value;
            sourceVariantInput.value = newVariantId || '';
            
            // Trigger change event to notify any listeners (like apps) that the variant changed
            sourceVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          
                    // Re-render the submit button to update any variant-specific changes
          const sourceSubmitButton = sourceBuyButtons.querySelector('.product-form__submit');
          const destinationSubmitButton = destinationBuyButtons.querySelector('.product-form__submit');
          
          if (sourceSubmitButton && destinationSubmitButton) {
            // Copy all attributes to ensure proper state
            Array.from(destinationSubmitButton.attributes).forEach(attr => {
              sourceSubmitButton.setAttribute(attr.name, attr.value);
            });
            
            // Update innerHTML after attributes to ensure proper state
            sourceSubmitButton.innerHTML = destinationSubmitButton.innerHTML;
            
            // Ensure the button is enabled if it doesn't have disabled attribute
            if (!destinationSubmitButton.hasAttribute('disabled')) {
              sourceSubmitButton.removeAttribute('disabled');
            }
          }
        }
      }

      initSellingPlanPriceSync() {
        const observer = new MutationObserver(() => {
          const stayWidget = document.querySelector('purchase-options');
          if (!stayWidget) return;

          observer.disconnect();

          stayWidget.addEventListener('change', (event) => {
            if (event.target.name === 'purchaseType' || event.target.name === 'stayai_selling_plan') {
              this.updateBuyButtonPriceForSellingPlan();
            }
          });

          this.updateBuyButtonPriceForSellingPlan();
        });

        observer.observe(document.body, { childList: true, subtree: true });
      }

      updateBuyButtonPriceForSellingPlan() {
        const subscriptionRadio = document.querySelector('#purchaseTypeSubscription');
        const sellingPlanSelect = document.querySelector('select[name="stayai_selling_plan"]');
        const priceEl = this.querySelector('.buy-button__price');
        if (!priceEl) return;

        const variant = this.getSelectedVariant(this);
        if (!variant) return;

        const isSubscription = subscriptionRadio?.checked;

        if (isSubscription && sellingPlanSelect && variant.selling_plan_allocations?.length) {
          const selectedPlanId = parseInt(sellingPlanSelect.value);
          const allocation = variant.selling_plan_allocations.find(a => a.selling_plan_id === selectedPlanId)
            || variant.selling_plan_allocations[0];

          priceEl.innerHTML = this.formatMoney(allocation.price);
        } else {
          priceEl.innerHTML = this.formatMoney(variant.price);
        }
      }

      formatMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));        
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('[data-slider-container] ul');
        const mediaGalleryDestination = html.querySelector(`[data-slider-container] ul`);

        const thumbGallerySource = this.querySelector('[data-slider-container] .thumbs-container');
        const thumbGalleryDestination = html.querySelector(`[data-slider-container] .thumbs-container`);

        // Update thumbnails with opacity transition
        if (thumbGallerySource && thumbGalleryDestination) {
          const thumbWrapper = this.querySelector('.swiper-thumbs');
          
          // Hide thumbnails during update
          if (thumbWrapper) {
            thumbWrapper.classList.add('opacity-0');
          }
          
          // Replace thumbnail content
          thumbGallerySource.innerHTML = thumbGalleryDestination.innerHTML;
          
          // Reinitialize Swiper for thumbnails
          const thumbSwiper = thumbWrapper?.querySelector('.swiper-thumbs');
          if (thumbSwiper && thumbSwiper.swiper) {
            thumbSwiper.swiper.destroy(true, true);
          }
          
          // Show thumbnails again
          if (thumbWrapper) {
            setTimeout(() => thumbWrapper.classList.remove('opacity-0'), 100)
            
          }
        }

        let currentActiveIndex = 0
        if (mediaGallerySource && mediaGalleryDestination) {
          // Save current active swiper index before replacing content
          const currentSwiper = this.querySelector('[data-slider-container] .swiper');
          currentActiveIndex = currentSwiper?.swiper?.activeIndex || 0;

          // Simply replace all media items with the new ones
          mediaGallerySource.innerHTML = mediaGalleryDestination.innerHTML;
        }

        // set featured media as active in the media gallery
        this.querySelector(`[data-media-gallery]`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        
        // Trigger custom event for media update
        this.dispatchEvent(new CustomEvent('variant:update-media', { 
          bubbles: true,
          detail: { 
            variantFeaturedMediaId,
            sectionId: this.dataset.section,
            currentActiveIndex
          }
        }));
        
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
